import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const JavierComponent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/javier/sessions`);
      setSessions(res.data.sessions || []);
    } catch (err) { console.error(err); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/javier/history/${sid}`);
      setMessages((res.data.history || []).map(h => ({
        role: h.role, content: h.content, timestamp: h.created_at
      })));
      setSessionId(sid);
      setShowSessions(false);
    } catch (err) { console.error(err); }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/javier/chat`, {
        message: userMsg,
        session_id: sessionId
      });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant', content: res.data.response, timestamp: res.data.timestamp
      }]);
      fetchSessions();
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Error de comunicacion con JAVIER, Mi Senor.';
      setMessages(prev => [...prev, { role: 'error', content: errMsg, timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const quickCommands = [
    { label: 'Estado del sistema', msg: 'Dame un resumen completo del estado actual del sistema' },
    { label: 'Marketplace', msg: 'Analiza la actividad del marketplace: ventas, listados activos, tendencias' },
    { label: 'Economia', msg: 'Como esta la economia del ecosistema? Balance total, velocity, salud general' },
    { label: 'Compliance', msg: 'Dame el reporte de compliance: usuarios de riesgo, alertas, anomalias detectadas' },
    { label: 'Anomalias', msg: 'Detectas alguna anomalia o riesgo en el sistema?' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-200px)]" data-testid="javier-component">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-t-xl p-4 border-b border-cyan-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-cyan-50" data-testid="javier-title">JAVIER</h2>
              <p className="text-xs text-cyan-400/70">Asistente de Inteligencia Avanzada — Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSessions(!showSessions)} data-testid="javier-sessions-btn"
              className="px-3 py-1.5 text-xs bg-slate-700/50 text-cyan-300 rounded-lg hover:bg-slate-700 border border-cyan-800/30 transition-colors">
              Sesiones
            </button>
            <button onClick={newSession} data-testid="javier-new-session-btn"
              className="px-3 py-1.5 text-xs bg-cyan-600/20 text-cyan-300 rounded-lg hover:bg-cyan-600/30 border border-cyan-500/30 transition-colors">
              + Nueva
            </button>
          </div>
        </div>

        {/* Sessions panel */}
        {showSessions && (
          <div className="mt-3 bg-slate-800/80 rounded-lg border border-slate-700 max-h-40 overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="p-3 text-xs text-slate-500">Sin sesiones previas</p>
            ) : sessions.map(s => (
              <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                data-testid={`session-${s.session_id}`}
                className={`w-full text-left p-2.5 text-xs hover:bg-slate-700/50 border-b border-slate-700/50 transition-colors ${
                  sessionId === s.session_id ? 'bg-cyan-900/20 text-cyan-300' : 'text-slate-400'
                }`}>
                <p className="truncate">{s.last_message}</p>
                <p className="text-slate-600 mt-0.5">{s.message_count} msgs</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto bg-slate-950 p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-cyan-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/>
              </svg>
            </div>
            <p className="text-cyan-400/60 text-sm font-medium">JAVIER en linea · Mayordomo Mayor</p>
            <p className="text-slate-600 text-xs mt-1 max-w-sm">Asistente exclusivo del administrador. Analizo datos, detecto anomalias y ejecuto comandos.</p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {quickCommands.map((cmd, i) => (
                <button key={i} onClick={() => { setInput(cmd.msg); inputRef.current?.focus(); }}
                  data-testid={`quick-cmd-${i}`}
                  className="px-3 py-1.5 text-xs bg-slate-800 text-cyan-400/70 rounded-lg border border-slate-700 hover:border-cyan-700/50 hover:text-cyan-300 transition-all">
                  {cmd.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`javier-msg-${idx}`}>
            <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
              msg.role === 'user'
                ? 'bg-cyan-600/20 text-cyan-100 border border-cyan-700/30 rounded-br-sm'
                : msg.role === 'error'
                  ? 'bg-red-900/30 text-red-300 border border-red-800/30'
                  : 'bg-slate-800/80 text-slate-200 border border-slate-700/50 rounded-bl-sm'
            }`}>
              {msg.role === 'assistant' && (
                <p className="text-[10px] text-cyan-500/60 font-medium mb-1.5 uppercase tracking-wider">JAVIER</p>
              )}
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
              {msg.timestamp && (
                <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-cyan-500/40' : 'text-slate-600'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-800/80 border border-slate-700/50 rounded-xl px-4 py-3">
              <p className="text-[10px] text-cyan-500/60 font-medium mb-1.5 uppercase tracking-wider">JAVIER</p>
              <div className="flex items-center space-x-1.5">
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}}></span>
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}></span>
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="bg-slate-900 border-t border-cyan-900/30 rounded-b-xl p-3">
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="text" value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe un comando o pregunta..."
            disabled={loading}
            data-testid="javier-input"
            className="flex-1 bg-slate-800 text-cyan-50 placeholder-slate-600 border border-slate-700 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-600/50 focus:ring-1 focus:ring-cyan-600/20 disabled:opacity-50" />
          <button type="submit" disabled={loading || !input.trim()} data-testid="javier-send-btn"
            className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-500 disabled:opacity-30 disabled:hover:bg-cyan-600 transition-colors">
            {loading ? '...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JavierComponent;
